import { Box, Paper, Tooltip, Typography } from "@mui/material";
import { formatNumber } from "./utils";

type Props = {
	variant: "actual" | "scheduled";
	title: string;
	finishedTons: number | null;
	avgYield: number | null;
	avgTph: number | null;
};

const Row = ({
	label,
	description,
	value,
}: {
	label: string;
	description: string;
	value: string | null;
}) => (
	<Box
		sx={{
			display: "flex",
			justifyContent: "space-between",
			alignItems: "baseline",
			py: 0.25,
		}}
	>
		<Tooltip arrow placement="top" title={description}>
			<Typography
				sx={{
					fontSize: "0.72rem",
					color: "#777",
				}}
			>
				{label}
			</Typography>
		</Tooltip>
		<Typography
			sx={{
				fontSize: value ? "0.8rem" : "0.72rem",
				fontWeight: 600,
				color: value ? "#1a1a1a" : "#777",
			}}
		>
			{value ?? "-"}
		</Typography>
	</Box>
);

export const MonthlyMetricsPanel = ({
	variant,
	title,
	finishedTons,
	avgYield,
	avgTph,
}: Props) => {
	return (
		<Paper
			elevation={0}
			sx={{
				p: 1.25,
				border: `1px ${variant === "actual" ? "solid" : "dashed"} #e0e0e0`,
				borderRadius: 1,
				backgroundColor: "#fff",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				flex: 1,
				minWidth: 0,
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
					<Box
						sx={{
							width: 7,
							height: 7,
							borderRadius: "50%",
							backgroundColor: (theme) =>
								variant === "actual"
									? theme.palette.primary.main
									: theme.palette.success.main,
						}}
					/>
					<Typography
						sx={{
							fontSize: "0.68rem",
							fontWeight: 700,
							color: (theme) =>
								variant === "actual"
									? theme.palette.primary.main
									: theme.palette.success.main,
							textTransform: "uppercase",
							letterSpacing: 0.5,
						}}
					>
						{title}
					</Typography>
				</Box>
			</Box>

			<Box>
				<Row
					label="Finished Tons"
					description={`${variant === "actual" ? "Actual" : "Scheduled"} Finished Tons for this month.`}
					value={formatNumber(finishedTons, 0)}
				/>
				<Row
					label="Avg Finished Yield"
					description={
						"Weighted " +
						(variant === "actual" ? "Actual" : "Scheduled") +
						" Yield for this month."
					}
					value={avgYield != null ? `${formatNumber(avgYield, 1)}%` : "-"}
				/>
				<Row
					label="Avg TPH"
					description={
						"Weighted " +
						(variant === "actual" ? "Actual" : "Budget") +
						" Tons Per Hour for this month."
					}
					value={formatNumber(avgTph, 1)}
				/>
			</Box>
		</Paper>
	);
};
